import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { CommonErrorType } from '../../common/enums.js';
import { useEppTerminate } from '../../common/hooks/useEppTerminate';
import {
  contactEmailAddressMsg,
  contactPhoneNumberMsg,
  deviceMsg,
  imeiSlashSerialNumberMsg,
  shippingAddressMsg,
  t,
  terminateAgreementAndReturnDeviceMsg,
  terminationIsBindingMsg,
  vatMsg,
} from '../../common/i18n/index.js';
import { formatSum } from '../../common/utils/priceUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { DeliveryAddressForm } from '../DeliveryAddressForm/DeliveryAddressForm.js';
import { getFullNameFromAuthenticatedUser } from '../EppMaintenance/eppMaintenanceUtils.js';
import { Loading } from '../Loading/index.js';
import type { EppTerminateDialogParams } from '../../common/types/dialog.js';
import type { CommonError } from '../../common/types/errors.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { DeliveryAddress, Subscription } from '../../generated/api/models.js';

export interface EppTerminateDialogProps {
  params: EppTerminateDialogParams;
  onCloseDialog: () => void;
  errors?: CommonError[];
  isLoading?: boolean;
  isEmployee: boolean;
}

export interface DeviceDetailsProps {
  subscription: EmployeeSubscription | Subscription;
  mobile: string;
  email: string;
}

export interface TerminationInfoProps {
  terminationPrice?: number;
  isEmployee: boolean;
}

const DeviceDetails = ({ subscription, mobile, email }: DeviceDetailsProps) => (
  <CL.Description
    items={[
      {
        title: t.TPVQ(deviceMsg),
        description: subscription.subscriptionName,
      },
      {
        title: subscription.details?.device?.serialNumber ? t.OE45(imeiSlashSerialNumberMsg) : '',
        description: subscription.details?.device?.serialNumber,
      },
      {
        title: t.HUN9(contactPhoneNumberMsg),
        description: mobile,
      },
      {
        title: t.S5PK(contactEmailAddressMsg),
        description: email,
      },
    ]}
    className={`${dsClass.MARGIN_0} ${dsClass.PADDING_BOTTOM_3}`}
  />
);

const TerminationInfo = ({ terminationPrice, isEmployee }: TerminationInfoProps) => (
  <div className={`${dsClass.PADDING_TOP_3} ${dsClass.PADDING_BOTTOM_3}`}>
    {terminationPrice !== undefined &&
      terminationPrice > 0 &&
      !isEmployee &&
      `${t.OOWX(
        'If you terminate an Elisa Palvelupäätelaite (service device) agreement mid-term, Elisa will charge you for the remaining monthly fees of the device as a one-time fee, for this device the amount is'
      )} ${formatSum(terminationPrice)} (${t.UZ6X(vatMsg)} 0%). `}
  </div>
);

const ShippingAddressInfo = () => (
  <>
    <div className={`${dsClass.PADDING_TOP_3} ${dsClass.PADDING_BOTTOM_3}`}>
      <strong>{t.IHO6(shippingAddressMsg)}</strong>
    </div>
    <div className={dsClass.PADDING_BOTTOM_4}>
      {t.WHHI('Select the delivery address below to which we will deliver the device-return bag.')}
    </div>
  </>
);

const ReturnPolicyInfo = () => (
  <div className={`${dsClass.PADDING_TOP_4} ${dsClass.PADDING_BOTTOM_2}  `}>
    {t.SONR(
      'The device must be returned to Elisa within one week. A penalty fee will be charged for unreturned devices.'
    )}
  </div>
);

export const EppTerminateDialog = ({
  params,
  errors,
  onCloseDialog,
  isLoading,
  isEmployee,
}: EppTerminateDialogProps) => {
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const companyInfo = params.companyInfo;
  const { authenticatedUser } = useAuth();
  const postEppTerminate = useEppTerminate(params.subscription, isEmployee);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress>();
  const companyName = companyInfo?.companyName || '';
  const email = authenticatedUser?.email || '';
  const mobile = authenticatedUser?.mobile || '';
  const apiErrors = errors ? errors.filter(err => err.type !== CommonErrorType.VALIDATION) : undefined;
  const terminationPrice = params.subscription.details!.device!.eppModificationPrices?.terminate;

  return (
    <ConfirmationDialog
      errors={apiErrors}
      wide={true}
      header={t.LIUF(terminateAgreementAndReturnDeviceMsg)}
      body={
        companyInfo ? (
          <div className={dsClass.PADDING_BOTTOM_3}>
            {t.MB11(terminationIsBindingMsg)}
            <TerminationInfo terminationPrice={terminationPrice} isEmployee={isEmployee} />
            <DeviceDetails subscription={params.subscription} mobile={mobile} email={email} />
            <ShippingAddressInfo />
            <DeliveryAddressForm
              address={companyInfo.address}
              companyName={companyName}
              recipientName={getFullNameFromAuthenticatedUser(authenticatedUser)}
              phoneNumber={mobile}
              onAddressReady={setIsSubmitEnabled}
              setDeliveryAddress={setDeliveryAddress}
            />
            <ReturnPolicyInfo />
          </div>
        ) : (
          <Loading />
        )
      }
      disableConfirm={!isSubmitEnabled || !companyInfo || !deliveryAddress}
      isLoading={isLoading}
      onCloseDialog={onCloseDialog}
      onConfirm={() => {
        postEppTerminate(deliveryAddress!); // confirm will be disabled unless deliveryAddress is defined
      }}
    />
  );
};
